import { memo } from "react";
import type { Dir } from "../demand/demandMath";
import { METRICS, describe, fmtDelta, verdict } from "./scorecardMath.ts";
import type { SimResult } from "./types.ts";

export interface Run {
  label: string;
  result: SimResult;
}

interface Props {
  /** One day, or two to compare: the second is read against the first. */
  runs: Run[];
  dirNames: Record<Dir, string>;
}

const VERDICT_WORD = { better: "better", worse: "worse", same: "", changed: "" } as const;

/**
 * The day in figures. One column per timetable and, with two, what changed —
 * each change saying in words whether it is an improvement, so the colour on
 * it is never the only reading.
 */
function Scorecard({ runs, dirNames }: Props) {
  const compare = runs.length === 2;
  return (
    <div className="dm-chart sim-score">
      <div className="dm-chart-head">
        <div>
          <h4>How the day goes</h4>
          <p className="dm-chart-sub">
            {compare
              ? "The two timetables against the same riders, and what the draft changes."
              : "The timetable against the measured riders, averaged over the selected days."}
          </p>
        </div>
      </div>

      <div className="sim-narratives">
        {runs.map((run) => {
          const n = describe(run.result, dirNames);
          return (
            <p key={run.label} className="sim-narrative">
              <strong>{run.label}.</strong> {n.text}
            </p>
          );
        })}
      </div>

      <div className="dm-table-scroll">
        <table className="data-table compact dm-table sim-score-table">
          <thead>
            <tr>
              <th>Over the day</th>
              {runs.map((run) => <th key={run.label} className="num">{run.label}</th>)}
              {compare && <th className="num">Change</th>}
            </tr>
          </thead>
          <tbody>
            {METRICS.map((m) => {
              const values = runs.map((run) => run.result.totals[m.key]);
              const v = compare ? verdict(m, values[0], values[1]) : null;
              return (
                <tr key={m.key}>
                  <th scope="row">
                    {m.label}
                    {m.note && <span className="sim-metric-note">{m.note}</span>}
                  </th>
                  {values.map((value, i) => (
                    <td key={i} className="num">{m.format(value)}</td>
                  ))}
                  {compare && v && (
                    <td className={`num sim-delta sim-delta-${v}`}>
                      {fmtDelta(m, values[0], values[1])}
                      {VERDICT_WORD[v] && <small> {VERDICT_WORD[v]}</small>}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default memo(Scorecard);
